import {
  FilterOperator,
  OrderType,
  useSearchQSetQuestionsSnapshotQuery,
} from '@exam-management/graphql';
import {
  useGetQSetQuestionsWithImage,
  type QSetQuestionWithQuestionSnapshot,
} from '@exam-management/qset-management';
import { NetworkStatus } from '@shared/apollo';
import { useEffect, useState } from 'react';

interface GetPaginatedQSetQuestionsDetailListVariable {
  qSetId: string;
  page: number;
  pageSize: number;
}

export function useGetPaginatedQSetQuestionsSnapshotDetailsList(
  variable: Readonly<GetPaginatedQSetQuestionsDetailListVariable>,
) {
  const { qSetId, page, pageSize } = variable;

  const { getQSetQuestionsSnapshotWithImage, loading: isGettingQSetQuestionWithImage } =
    useGetQSetQuestionsWithImage();

  const [qSetQuestions, setQSetQuestions] = useState<QSetQuestionWithQuestionSnapshot[]>([]);

  // NOTE: since we setState after setQSetQuestionId, we can't use loading from Apollo, because it'll be false before finish setState
  const [isQuestionsLoading, setIsQuestionsLoading] = useState(true);

  const {
    networkStatus,
    refetch: refetchQSetQuestions,
    loading: isSearchingQSetQuestions,
  } = useSearchQSetQuestionsSnapshotQuery({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      input: {
        paging: {
          currentPage: page,
          pageSize: pageSize,
        },
        order: {
          field: 'order',
          type: OrderType.Asc,
        },
        filter: {
          conditions: [
            {
              field: 'qSetId',
              value: qSetId,
              operator: FilterOperator.Equal,
            },
          ],
        },
      },
    },
    onCompleted: (data) => {
      (async () => {
        const qSetQuestionsWithImage = await getQSetQuestionsSnapshotWithImage(
          data.excSearchQSetQuestions.qSetQuestions,
        );
        setQSetQuestions(
          qSetQuestionsWithImage.filter(
            (qSetQuestion): qSetQuestion is QSetQuestionWithQuestionSnapshot => {
              return (
                !!qSetQuestion.questionSnapshot &&
                qSetQuestion.questionSnapshot.questionGroup?.type !== undefined
              );
            },
          ),
        );
        setIsQuestionsLoading(false);
      })();
    },
  });

  useEffect(() => {
    if (isSearchingQSetQuestions) {
      setIsQuestionsLoading(true);
    }
  }, [page]);

  const refetch = async () => {
    setIsQuestionsLoading(true);
    await refetchQSetQuestions();
  };

  const isLoading = [
    NetworkStatus.loading,
    NetworkStatus.setVariables,
    NetworkStatus.refetch,
  ].includes(networkStatus);

  return {
    data: qSetQuestions,
    loading:
      isLoading || isGettingQSetQuestionWithImage || isQuestionsLoading || isSearchingQSetQuestions,
    refetch,
  };
}
